import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from '@mui/x-data-grid/locales';
import { IPJsApi } from './types';
import { cpfMask } from "utils/cpf";
import dayjs from "dayjs";

type Props = {
  data: IPJsApi['people'];
}

export function TableView({ data }: Props) {
  // DataGrid columns including all fields from the updated IPJsApi type
  // Organized by field groups: common, employee-specific, dependent-specific, and timestamps
  const realColumns: GridColDef<(typeof realRows)[number]>[] = [
    // Common fields
    // { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Nome completo", width: 350 },
    { field: "taxPayerId", headerName: "CPF beneficiário", width: 150 },
    { field: "taxPayerIdEmployee", headerName: "CPF titular", width: 150 },
    { field: "birthDate", headerName: "Data de nascimento", width: 150 },
    { field: "motherName", headerName: "Nome da mãe", width: 200 },
    { field: "weddingDate", headerName: "Data de casamento", width: 150 },
    { field: "type", headerName: "Tipo", width: 120 },
    
    // Employee specific fields
    { field: "organizationId", headerName: "ID Organização", width: 150 },
    { field: "cnpj", headerName: "CNPJ empresa", width: 150 },
    { field: "pis", headerName: "PIS", width: 130 },
    { field: "genderId", headerName: "Gênero", width: 100 },
    { field: "raceId", headerName: "Raça", width: 100 },
    { field: "maritalStatusId", headerName: "Estado Civil", width: 120 },
    { field: "educationLevelId", headerName: "Escolaridade", width: 120 },
    { field: "socialName", headerName: "Nome Social", width: 200 },
    { field: "birthCountryId", headerName: "País Nascimento", width: 130 },
    { field: "nationalityCountryId", headerName: "Nacionalidade", width: 120 },
    { field: "birthStateId", headerName: "Estado Nascimento", width: 130 },
    { field: "birthCityId", headerName: "Cidade Nascimento", width: 130 },
    { field: "fatherName", headerName: "Nome do Pai", width: 200 },
    { field: "zipCode", headerName: "CEP", width: 100 },
    { field: "addressType", headerName: "Tipo Endereço", width: 120 },
    { field: "address", headerName: "Endereço", width: 250 },
    { field: "addressNumber", headerName: "Número", width: 100 },
    { field: "complement", headerName: "Complemento", width: 150 },
    { field: "neighborhood", headerName: "Bairro", width: 150 },
    { field: "stateId", headerName: "Estado", width: 100 },
    { field: "cityId", headerName: "Cidade", width: 130 },
    { field: "nationalIdentityNumber", headerName: "RG", width: 130 },
    { field: "nationalIdentityEmissor", headerName: "Órgão Emissor RG", width: 130 },
    { field: "nationalIdentityExpeditionDate", headerName: "Data Expedição RG", width: 150 },
    { field: "phoneNumber", headerName: "Telefone", width: 130 },
    { field: "email", headerName: "E-mail", width: 200 },
    { field: "hiringDate", headerName: "Data de admissão", width: 150 },
    
    // New fields from layoutFields.txt
    { field: "status", headerName: "Status", width: 120 },
    { field: "jobTitle", headerName: "Cargo", width: 180 },
    { field: "paymentFrequency", headerName: "Periodicidade Pagamento", width: 180 },
    { field: "grossPayment", headerName: "Pagamento Bruto", width: 150 },
    { field: "municipality", headerName: "Município", width: 150 },
    { field: "cbo", headerName: "CBO", width: 100 },
    { field: "dismissalDate", headerName: "Data Demissão", width: 150 },
    { field: "transferred", headerName: "Transferido", width: 120 },
    { field: "plan", headerName: "Plano", width: 150 },
    
    // Dependent specific fields
    { field: "employeeId", headerName: "ID Funcionário", width: 130 },
    { field: "dependentTypeId", headerName: "Tipo Dependente", width: 130 },
    // { field: "isDeleted", headerName: "Excluído", width: 100 },
    { field: "isStudent", headerName: "Estudante", width: 100 },
    
    // Timestamps
    // { field: "createdAt", headerName: "Criado em", width: 150 },
    // { field: "updatedAt", headerName: "Atualizado em", width: 150 },
  ];

  // Map all fields from the API response to DataGrid rows
  // Apply proper formatting for dates, booleans, and null values
  const realRows = data.map((row, index) => ({
    // Using row.id if available, otherwise use index
    id: row?.id ?? `generated_${index + 1}`,
    
    // Common fields
    name: row?.name ?? '-',
    taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '-',
    taxPayerIdEmployee: !!row?.employeeTaxPayerId ? cpfMask(row.employeeTaxPayerId) : '-',
    birthDate: !!row?.birthDate ? dayjs(row.birthDate).format("DD/MM/YYYY") : '-',
    motherName: row?.motherName ? row.motherName.toUpperCase() : '-',
    weddingDate: !!row?.weddingDate ? dayjs(row.weddingDate).format("DD/MM/YYYY") : '-',
    type: row?.type ?? '-',
    
    // Employee specific fields
    organizationId: row?.organizationId ?? '-',
    cnpj: row?.cnpj ?? '-',
    pis: row?.pis ?? '-',
    genderId: row?.genderId ?? '-',
    raceId: row?.raceId ?? '-',
    maritalStatusId: row?.maritalStatusId ?? '-',
    educationLevelId: row?.educationLevelId ?? '-',
    socialName: row?.socialName ?? '-',
    birthCountryId: row?.birthCountryId ?? '-',
    nationalityCountryId: row?.nationalityCountryId ?? '-',
    birthStateId: row?.birthStateId ?? '-',
    birthCityId: row?.birthCityId ?? '-',
    fatherName: row?.fatherName ?? '-',
    zipCode: row?.zipCode ?? '-',
    addressType: row?.addressType ?? '-',
    address: row?.address ?? '-',
    addressNumber: row?.addressNumber ?? '-',
    complement: row?.complement ?? '-',
    neighborhood: row?.neighborhood ?? '-',
    stateId: row?.stateId ?? '-',
    cityId: row?.cityId ?? '-',
    nationalIdentityNumber: row?.nationalIdentityNumber ?? '-',
    nationalIdentityEmissor: row?.nationalIdentityEmissor ?? '-',
    nationalIdentityExpeditionDate: row?.nationalIdentityExpeditionDate ?? '-',
    phoneNumber: row?.phoneNumber ?? '-',
    email: row?.email ?? '-',
    hiringDate: !!row?.hiringDate ? dayjs(row.hiringDate).format("DD/MM/YYYY") : '-',
    
    // New fields from layoutFields.txt
    status: row?.status ?? '-',
    jobTitle: row?.jobTitle ?? '-',
    paymentFrequency: row?.paymentFrequency ?? '-',
    grossPayment: row?.grossPayment ?? '-',
    municipality: row?.municipality ?? '-',
    cbo: row?.cbo ?? '-',
    dismissalDate: !!row?.dismissalDate ? dayjs(row.dismissalDate).format("DD/MM/YYYY") : '-',
    transferred: row?.transferred ?? '-',
    plan: row?.plan ?? '-',
    
    // Dependent specific fields
    employeeId: row?.employeeId ?? '-',
    dependentTypeId: row?.dependentTypeId ?? '-',
    isDeleted: row?.isDeleted !== null ? (row.isDeleted ? 'Sim' : 'Não') : '-',
    isStudent: row?.isStudent !== null ? (row.isStudent ? 'Sim' : 'Não') : '-',
    
    // Timestamps
    createdAt: !!row?.createdAt ? dayjs(row.createdAt).format("DD/MM/YYYY HH:mm") : '-',
    updatedAt: !!row?.updatedAt ? dayjs(row.updatedAt).format("DD/MM/YYYY HH:mm") : '-',
  }));

  return (
    <DataGrid
      rows={realRows}
      columns={realColumns}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
        // columns: {
        //   columnVisibilityModel: {
        //     // Hide some less common fields by default to improve UX
        //     organizationId: false,
        //     birthCountryId: false,
        //     nationalityCountryId: false,
        //     birthStateId: false,
        //     birthCityId: false,
        //     stateId: false,
        //     cityId: false,
        //     educationLevelId: false,
        //     raceId: false,
        //     addressType: false,
        //     nationalIdentityEmissor: false,
        //     nationalIdentityExpeditionDate: false,
        //     dependentTypeId: false,
        //     createdAt: false,
        //     updatedAt: false,
        //   },
        // },
      }}
      pageSizeOptions={[5, 10, 20, 50]}
      disableRowSelectionOnClick
      style={{ backgroundColor: "white", minHeight: 400 }}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      // Enable column filtering, sorting, and hiding
      disableColumnFilter={false}
      disableColumnSelector={false}
      disableDensitySelector={false}
      // Enable scrollbars for better navigation with many columns
      scrollbarSize={10}
    />
  )
}